import { Injectable, inject } from "@angular/core";
import type { Collector, CollectorMechanism, CollectorShape } from "@partikelart/collector/domain";
import type {
	AreaMeasurement,
	CreateAreaPayload,
	CreateMeasurementPayload,
	EnvironmentApiPort,
	PositionLookup,
} from "@partikelart/environment/data-access-core";
import type {
	FactoryLayout,
	MonitoringArea,
	TrapLimit,
	TrapMeasurement,
	TrapPosition,
} from "@partikelart/environment/domain";
import { LimitType, MaterialType, MeasurementSource, TrapPositionStatus } from "@partikelart/environment/domain";
import { type MatchablePosition, parseLabReport, rankPositionCandidates } from "@partikelart/environment/domain-clean";
import type {
	BookTrapCollectorDto,
	CreateTrapLimitDto,
	LabReportCommitItemDto,
	LabReportCommitResultDto,
	ParsedLabReportFileDto,
	ParsedSurfaceReportFileDto,
	SurfaceImportSizeClassDto,
	SurfaceReportCommitItemDto,
	SurfaceReportCommitResultDto,
	UpdateFactoryLayoutDto,
	UpdateMonitoringAreaDto,
	UpdateTrapLimitDto,
	UpdateTrapMeasurementDto,
	UpdateTrapPositionDto,
} from "@partikelart/environment/dtos";
import {
	emptySizeClassBin,
	Material,
	type ParticleCollection,
	type SizeClassBin,
	toSizeClass,
} from "@partikelart/particles/domain";
import { DemoSurfaceReportRepository } from "@partikelart/surface/data-access-demo";
import { parsePicedAsciiReport } from "@partikelart/surface/domain";
import { delay, firstValueFrom, from, Observable, of } from "rxjs";

const DEMO_SITE_ID = "00000000-0000-4000-8000-000000000010";

const DEMO_LAYOUT_1: FactoryLayout = {
	id: "demo-layout-1",
	siteId: DEMO_SITE_ID,
	name: "Production Hall A",
	imageUrl: "/assets/images/layout.png",
	imageWidth: 1024,
	imageHeight: 1024,
	pixelsPerMeter: 100,
	areas: [
		{
			id: "demo-area-1",
			layoutId: "demo-layout-1",
			name: "Assembly Line 1",
			polygonPoints: [
				{ x: 100, y: 100 },
				{ x: 800, y: 100 },
				{ x: 800, y: 500 },
				{ x: 100, y: 500 },
			],
			color: "#3b82f6",
			trapSpacingM: 2,
			positions: [
				{
					id: "demo-pos-1",
					areaId: "demo-area-1",
					name: "TP-01",
					x: 200,
					y: 200,
					status: TrapPositionStatus.Active,
					activatedAt: new Date("2026-06-01T00:00:00Z"),
					deactivatedAt: null,
					activeCollectorId: "00000000-0000-0000-0000-000000000003",
					collectorBookedAt: new Date("2026-06-01T00:00:00Z"),
					notes: "",
					createdAt: new Date("2026-01-01T00:00:00Z"),
					updatedAt: new Date("2026-06-01T00:00:00Z"),
				},
				{
					id: "demo-pos-2",
					areaId: "demo-area-1",
					name: "TP-02",
					x: 400,
					y: 200,
					status: TrapPositionStatus.Active,
					activatedAt: new Date("2026-06-01T00:00:00Z"),
					deactivatedAt: null,
					activeCollectorId: "00000000-0000-0000-0000-000000000001",
					collectorBookedAt: new Date("2026-06-01T00:00:00Z"),
					notes: "",
					createdAt: new Date("2026-01-01T00:00:00Z"),
					updatedAt: new Date("2026-06-01T00:00:00Z"),
				},
				{
					id: "demo-pos-3",
					areaId: "demo-area-1",
					name: "TP-03",
					x: 600,
					y: 200,
					status: TrapPositionStatus.Active,
					activatedAt: new Date("2026-06-01T00:00:00Z"),
					deactivatedAt: null,
					activeCollectorId: null,
					collectorBookedAt: null,
					notes: "",
					createdAt: new Date("2026-01-01T00:00:00Z"),
					updatedAt: new Date("2026-06-01T00:00:00Z"),
				},
				{
					id: "demo-pos-4",
					areaId: "demo-area-1",
					name: "TP-04",
					x: 300,
					y: 400,
					status: TrapPositionStatus.Confirmed,
					activatedAt: null,
					deactivatedAt: null,
					activeCollectorId: null,
					collectorBookedAt: null,
					notes: "",
					createdAt: new Date("2026-01-01T00:00:00Z"),
					updatedAt: new Date("2026-01-01T00:00:00Z"),
				},
				{
					id: "demo-pos-5",
					areaId: "demo-area-1",
					name: "TP-05",
					x: 500,
					y: 400,
					status: TrapPositionStatus.Inactive,
					activatedAt: null,
					deactivatedAt: new Date("2026-06-10T00:00:00Z"),
					activeCollectorId: null,
					collectorBookedAt: null,
					notes: "Temporarily off",
					createdAt: new Date("2026-01-01T00:00:00Z"),
					updatedAt: new Date("2026-06-10T00:00:00Z"),
				},
			],
			createdAt: new Date("2026-01-01T00:00:00Z"),
			updatedAt: new Date("2026-01-01T00:00:00Z"),
		},
		{
			id: "demo-area-2",
			layoutId: "demo-layout-1",
			name: "Packaging Zone",
			polygonPoints: [
				{ x: 100, y: 520 },
				{ x: 500, y: 520 },
				{ x: 500, y: 680 },
				{ x: 100, y: 680 },
			],
			color: "#ed3b95",
			trapSpacingM: 3,
			positions: [
				{
					id: "demo-pos-6",
					areaId: "demo-area-2",
					name: "PK-01",
					x: 200,
					y: 600,
					status: TrapPositionStatus.Active,
					activatedAt: new Date("2026-06-01T00:00:00Z"),
					deactivatedAt: null,
					activeCollectorId: null,
					collectorBookedAt: null,
					notes: "",
					createdAt: new Date("2026-01-01T00:00:00Z"),
					updatedAt: new Date("2026-06-01T00:00:00Z"),
				},
				{
					id: "demo-pos-7",
					areaId: "demo-area-2",
					name: "PK-02",
					x: 350,
					y: 600,
					status: TrapPositionStatus.Active,
					activatedAt: new Date("2026-06-01T00:00:00Z"),
					deactivatedAt: null,
					activeCollectorId: null,
					collectorBookedAt: null,
					notes: "",
					createdAt: new Date("2026-01-01T00:00:00Z"),
					updatedAt: new Date("2026-06-01T00:00:00Z"),
				},
			],
			createdAt: new Date("2026-01-01T00:00:00Z"),
			updatedAt: new Date("2026-01-01T00:00:00Z"),
		},
		{
			id: "demo-area-4",
			layoutId: "demo-layout-1",
			name: "Quality Control",
			polygonPoints: [
				{ x: 520, y: 520 },
				{ x: 800, y: 520 },
				{ x: 800, y: 900 },
				{ x: 520, y: 900 },
			],
			color: "#10b981",
			trapSpacingM: 2,
			positions: [
				{
					id: "demo-pos-8",
					areaId: "demo-area-4",
					name: "QC-01",
					x: 600,
					y: 600,
					status: TrapPositionStatus.Active,
					activatedAt: new Date("2026-06-01T00:00:00Z"),
					deactivatedAt: null,
					activeCollectorId: null,
					collectorBookedAt: null,
					notes: "",
					createdAt: new Date("2026-01-01T00:00:00Z"),
					updatedAt: new Date("2026-06-01T00:00:00Z"),
				},
				{
					id: "demo-pos-9",
					areaId: "demo-area-4",
					name: "QC-02",
					x: 700,
					y: 700,
					status: TrapPositionStatus.Active,
					activatedAt: new Date("2026-06-01T00:00:00Z"),
					deactivatedAt: null,
					activeCollectorId: null,
					collectorBookedAt: null,
					notes: "",
					createdAt: new Date("2026-01-01T00:00:00Z"),
					updatedAt: new Date("2026-06-01T00:00:00Z"),
				},
				{
					id: "demo-pos-10",
					areaId: "demo-area-4",
					name: "QC-03",
					x: 600,
					y: 800,
					status: TrapPositionStatus.Confirmed,
					activatedAt: null,
					deactivatedAt: null,
					activeCollectorId: null,
					collectorBookedAt: null,
					notes: "",
					createdAt: new Date("2026-01-01T00:00:00Z"),
					updatedAt: new Date("2026-01-01T00:00:00Z"),
				},
			],
			createdAt: new Date("2026-01-01T00:00:00Z"),
			updatedAt: new Date("2026-01-01T00:00:00Z"),
		},
	],
	createdAt: new Date("2026-01-01T00:00:00Z"),
	updatedAt: new Date("2026-01-01T00:00:00Z"),
};

const DEMO_LAYOUT_2: FactoryLayout = {
	id: "demo-layout-2",
	siteId: DEMO_SITE_ID,
	name: "Clean Room B",
	imageUrl: "/assets/images/layout.png",
	imageWidth: 1024,
	imageHeight: 1024,
	pixelsPerMeter: 100,
	areas: [
		{
			id: "demo-area-3",
			layoutId: "demo-layout-2",
			name: "ISO 7 Zone",
			polygonPoints: [
				{ x: 100, y: 100 },
				{ x: 500, y: 100 },
				{ x: 500, y: 500 },
				{ x: 100, y: 500 },
			],
			color: "#3e1463",
			trapSpacingM: 2,
			positions: [
				{
					id: "demo-pos-11",
					areaId: "demo-area-3",
					name: "CR-01",
					x: 150,
					y: 150,
					status: TrapPositionStatus.Active,
					activatedAt: new Date("2026-06-15T00:00:00Z"),
					deactivatedAt: null,
					activeCollectorId: "00000000-0000-0000-0000-000000000001",
					collectorBookedAt: new Date("2026-06-15T00:00:00Z"),
					notes: "",
					createdAt: new Date("2026-02-01T00:00:00Z"),
					updatedAt: new Date("2026-06-15T00:00:00Z"),
				},
				{
					id: "demo-pos-12",
					areaId: "demo-area-3",
					name: "CR-02",
					x: 300,
					y: 150,
					status: TrapPositionStatus.Active,
					activatedAt: new Date("2026-06-15T00:00:00Z"),
					deactivatedAt: null,
					activeCollectorId: null,
					collectorBookedAt: null,
					notes: "",
					createdAt: new Date("2026-02-01T00:00:00Z"),
					updatedAt: new Date("2026-06-15T00:00:00Z"),
				},
				{
					id: "demo-pos-13",
					areaId: "demo-area-3",
					name: "CR-03",
					x: 450,
					y: 150,
					status: TrapPositionStatus.Active,
					activatedAt: new Date("2026-06-15T00:00:00Z"),
					deactivatedAt: null,
					activeCollectorId: null,
					collectorBookedAt: null,
					notes: "",
					createdAt: new Date("2026-02-01T00:00:00Z"),
					updatedAt: new Date("2026-06-15T00:00:00Z"),
				},
				{
					id: "demo-pos-14",
					areaId: "demo-area-3",
					name: "CR-04",
					x: 200,
					y: 350,
					status: TrapPositionStatus.Active,
					activatedAt: new Date("2026-06-15T00:00:00Z"),
					deactivatedAt: null,
					activeCollectorId: null,
					collectorBookedAt: null,
					notes: "",
					createdAt: new Date("2026-02-01T00:00:00Z"),
					updatedAt: new Date("2026-06-15T00:00:00Z"),
				},
			],
			createdAt: new Date("2026-02-01T00:00:00Z"),
			updatedAt: new Date("2026-02-01T00:00:00Z"),
		},
		{
			id: "demo-area-5",
			layoutId: "demo-layout-2",
			name: "ISO 5 Zone",
			polygonPoints: [
				{ x: 520, y: 100 },
				{ x: 900, y: 100 },
				{ x: 900, y: 500 },
				{ x: 520, y: 500 },
			],
			color: "#f59e0b",
			trapSpacingM: 1.5,
			positions: [
				{
					id: "demo-pos-15",
					areaId: "demo-area-5",
					name: "ISO5-01",
					x: 600,
					y: 200,
					status: TrapPositionStatus.Active,
					activatedAt: new Date("2026-06-15T00:00:00Z"),
					deactivatedAt: null,
					activeCollectorId: null,
					collectorBookedAt: null,
					notes: "",
					createdAt: new Date("2026-02-01T00:00:00Z"),
					updatedAt: new Date("2026-06-15T00:00:00Z"),
				},
				{
					id: "demo-pos-16",
					areaId: "demo-area-5",
					name: "ISO5-02",
					x: 750,
					y: 200,
					status: TrapPositionStatus.Active,
					activatedAt: new Date("2026-06-15T00:00:00Z"),
					deactivatedAt: null,
					activeCollectorId: null,
					collectorBookedAt: null,
					notes: "",
					createdAt: new Date("2026-02-01T00:00:00Z"),
					updatedAt: new Date("2026-06-15T00:00:00Z"),
				},
				{
					id: "demo-pos-17",
					areaId: "demo-area-5",
					name: "ISO5-03",
					x: 600,
					y: 400,
					status: TrapPositionStatus.Confirmed,
					activatedAt: null,
					deactivatedAt: null,
					activeCollectorId: null,
					collectorBookedAt: null,
					notes: "",
					createdAt: new Date("2026-02-01T00:00:00Z"),
					updatedAt: new Date("2026-02-01T00:00:00Z"),
				},
			],
			createdAt: new Date("2026-02-01T00:00:00Z"),
			updatedAt: new Date("2026-02-01T00:00:00Z"),
		},
	],
	createdAt: new Date("2026-02-01T00:00:00Z"),
	updatedAt: new Date("2026-02-01T00:00:00Z"),
};

const DEMO_LAYOUT_3: FactoryLayout = {
	id: "demo-layout-3",
	siteId: DEMO_SITE_ID,
	name: "Warehouse C",
	imageUrl: "/assets/images/layout.png",
	imageWidth: 1024,
	imageHeight: 1024,
	pixelsPerMeter: 100,
	areas: [
		{
			id: "demo-area-6",
			layoutId: "demo-layout-3",
			name: "Storage Area",
			polygonPoints: [
				{ x: 50, y: 50 },
				{ x: 950, y: 50 },
				{ x: 950, y: 500 },
				{ x: 50, y: 500 },
			],
			color: "#8b5cf6",
			trapSpacingM: 4,
			positions: [
				{
					id: "demo-pos-18",
					areaId: "demo-area-6",
					name: "WH-01",
					x: 150,
					y: 150,
					status: TrapPositionStatus.Active,
					activatedAt: new Date("2026-05-01T00:00:00Z"),
					deactivatedAt: null,
					activeCollectorId: null,
					collectorBookedAt: null,
					notes: "",
					createdAt: new Date("2026-03-01T00:00:00Z"),
					updatedAt: new Date("2026-05-01T00:00:00Z"),
				},
				{
					id: "demo-pos-19",
					areaId: "demo-area-6",
					name: "WH-02",
					x: 400,
					y: 150,
					status: TrapPositionStatus.Active,
					activatedAt: new Date("2026-05-01T00:00:00Z"),
					deactivatedAt: null,
					activeCollectorId: null,
					collectorBookedAt: null,
					notes: "",
					createdAt: new Date("2026-03-01T00:00:00Z"),
					updatedAt: new Date("2026-05-01T00:00:00Z"),
				},
				{
					id: "demo-pos-20",
					areaId: "demo-area-6",
					name: "WH-03",
					x: 700,
					y: 150,
					status: TrapPositionStatus.Active,
					activatedAt: new Date("2026-05-01T00:00:00Z"),
					deactivatedAt: null,
					activeCollectorId: null,
					collectorBookedAt: null,
					notes: "",
					createdAt: new Date("2026-03-01T00:00:00Z"),
					updatedAt: new Date("2026-05-01T00:00:00Z"),
				},
				{
					id: "demo-pos-21",
					areaId: "demo-area-6",
					name: "WH-04",
					x: 150,
					y: 400,
					status: TrapPositionStatus.Inactive,
					activatedAt: null,
					deactivatedAt: new Date("2026-05-15T00:00:00Z"),
					activeCollectorId: null,
					collectorBookedAt: null,
					notes: "Damaged collector",
					createdAt: new Date("2026-03-01T00:00:00Z"),
					updatedAt: new Date("2026-05-15T00:00:00Z"),
				},
				{
					id: "demo-pos-22",
					areaId: "demo-area-6",
					name: "WH-05",
					x: 500,
					y: 400,
					status: TrapPositionStatus.Confirmed,
					activatedAt: null,
					deactivatedAt: null,
					activeCollectorId: null,
					collectorBookedAt: null,
					notes: "",
					createdAt: new Date("2026-03-01T00:00:00Z"),
					updatedAt: new Date("2026-03-01T00:00:00Z"),
				},
			],
			createdAt: new Date("2026-03-01T00:00:00Z"),
			updatedAt: new Date("2026-03-01T00:00:00Z"),
		},
	],
	createdAt: new Date("2026-03-01T00:00:00Z"),
	updatedAt: new Date("2026-03-01T00:00:00Z"),
};

const LAYOUTS = [DEMO_LAYOUT_1, DEMO_LAYOUT_2, DEMO_LAYOUT_3];

const DEMO_COLLECTORS: Collector[] = [
	{
		id: "00000000-0000-0000-0000-000000000001",
		name: "Collector A",
		mechanism: "adhesive" as CollectorMechanism,
		shape: "round" as CollectorShape,
		vendor: "Demo Vendor",
		sku: "DV-001",
		material: "PTFE",
		poreSizeUm: 0.45,
		diameterUm: 47000,
		widthUm: null,
		isSupported: true,
		createdAt: new Date("2026-01-01T00:00:00Z"),
		updatedAt: new Date("2026-01-01T00:00:00Z"),
	},
	{
		id: "00000000-0000-0000-0000-000000000003",
		name: "Collector B",
		mechanism: "mesh" as CollectorMechanism,
		shape: "round" as CollectorShape,
		vendor: "Demo Vendor",
		sku: "DV-003",
		material: "Nylon",
		poreSizeUm: 0.22,
		diameterUm: 47000,
		widthUm: null,
		isSupported: true,
		createdAt: new Date("2026-01-01T00:00:00Z"),
		updatedAt: new Date("2026-01-01T00:00:00Z"),
	},
	{
		id: "00000000-0000-0000-0000-000000000004",
		name: "Collector C",
		mechanism: "adhesive" as CollectorMechanism,
		shape: "round" as CollectorShape,
		vendor: "CleanTest",
		sku: "CT-200",
		material: "Polycarbonate",
		poreSizeUm: 0.4,
		diameterUm: 47000,
		widthUm: null,
		isSupported: true,
		createdAt: new Date("2026-01-01T00:00:00Z"),
		updatedAt: new Date("2026-01-01T00:00:00Z"),
	},
	{
		id: "00000000-0000-0000-0000-000000000005",
		name: "Collector D",
		mechanism: "mesh" as CollectorMechanism,
		shape: "square" as CollectorShape,
		vendor: "PartikelART",
		sku: "PA-MESH-1",
		material: "Stainless Steel",
		poreSizeUm: 0.63,
		diameterUm: null,
		widthUm: 48000,
		isSupported: true,
		createdAt: new Date("2026-01-01T00:00:00Z"),
		updatedAt: new Date("2026-01-01T00:00:00Z"),
	},
];

// ── Demo limits (area-level) ───────────────────────────────────────

const DEMO_LIMITS: TrapLimit[] = [
	{
		id: "demo-limit-1",
		areaId: "demo-area-1",
		materialType: MaterialType.Metal,
		limitType: LimitType.FeretMax,
		value: 500,
		createdAt: new Date("2026-06-01T00:00:00Z"),
		updatedAt: new Date("2026-06-01T00:00:00Z"),
	},
	{
		id: "demo-limit-2",
		areaId: "demo-area-2",
		materialType: MaterialType.Metal,
		limitType: LimitType.FeretMax,
		value: 300,
		createdAt: new Date("2026-06-01T00:00:00Z"),
		updatedAt: new Date("2026-06-01T00:00:00Z"),
	},
	{
		id: "demo-limit-3",
		areaId: "demo-area-4",
		materialType: MaterialType.Metal,
		limitType: LimitType.FeretMax,
		value: 400,
		createdAt: new Date("2026-06-01T00:00:00Z"),
		updatedAt: new Date("2026-06-01T00:00:00Z"),
	},
	{
		id: "demo-limit-4",
		areaId: "demo-area-3",
		materialType: MaterialType.Metal,
		limitType: LimitType.FeretMax,
		value: 200,
		createdAt: new Date("2026-06-15T00:00:00Z"),
		updatedAt: new Date("2026-06-15T00:00:00Z"),
	},
	{
		id: "demo-limit-5",
		areaId: "demo-area-5",
		materialType: MaterialType.Metal,
		limitType: LimitType.FeretMax,
		value: 100,
		createdAt: new Date("2026-06-15T00:00:00Z"),
		updatedAt: new Date("2026-06-15T00:00:00Z"),
	},
	{
		id: "demo-limit-6",
		areaId: "demo-area-6",
		materialType: MaterialType.Metal,
		limitType: LimitType.FeretMax,
		value: 600,
		createdAt: new Date("2026-05-01T00:00:00Z"),
		updatedAt: new Date("2026-05-01T00:00:00Z"),
	},
];

// ── Demo measurements ──────────────────────────────────────────────
// Multiple timestamps per position so the heatmap time slider is meaningful.
// Particle counts chosen to hit different color bands relative to limit=500.

const DEMO_TS = [
	new Date("2026-05-01T10:00:00Z"),
	new Date("2026-05-08T10:00:00Z"),
	new Date("2026-05-15T10:00:00Z"),
	new Date("2026-05-22T10:00:00Z"),
	new Date("2026-06-01T10:00:00Z"),
	new Date("2026-06-08T10:00:00Z"),
	new Date("2026-06-15T10:00:00Z"),
	new Date("2026-06-22T10:00:00Z"),
	new Date("2026-06-29T10:00:00Z"),
	new Date("2026-07-06T10:00:00Z"),
];

function makeParticleData(particleCount: number, maxFeretUm: number): ParticleCollection {
	const particles = [];
	for (let i = 0; i < particleCount; i++) {
		const feretMax = (Math.random() * maxFeretUm) / 5; // pixels, micronsPerPixel=5
		particles.push({
			hash: i + 1,
			exterior: [
				[0, 0],
				[10, 0],
				[10, 10],
				[0, 10],
			],
			holes: [],
			measurements: {
				feretMax: { distance: feretMax, angle: 0 },
				feretMin: { width: feretMax * 0.6, angle: 90 },
				inCircle: { radius: feretMax * 0.3 },
				areaPx: 100,
				computedClass: i % 3 === 0 ? 0 : 1, // 0=metal, 1=non_metal
			},
		} as any);
	}
	return {
		type: "ParticleCollection",
		version: 1,
		crs: { type: "image", micronsPerPixel: 5, imageDimensions: [1200, 1200] },
		metadata: {
			analysisDate: new Date().toISOString(),
			coverage: 0.15,
			mode: "trap",
			softwareVersion: "demo-1.0",
		},
		particles,
	};
}

interface DemoMeasurement extends TrapMeasurement {
	x: number;
	y: number;
}

const DEMO_MEASUREMENTS: DemoMeasurement[] = [
	// ── Layout 1, Area 1 (Assembly Line 1, limit=500) ──────────────
	// pos-1 (TP-01): starts green, gets worse over time (over limit at the end)
	{
		id: "dm-1-0",
		trapPositionId: "demo-pos-1",
		collectorId: null,
		measuredAt: DEMO_TS[0],
		measuredBy: "demo",
		particleData: makeParticleData(120, 300),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[0],
		x: 200,
		y: 200,
	},
	{
		id: "dm-1-1",
		trapPositionId: "demo-pos-1",
		collectorId: null,
		measuredAt: DEMO_TS[1],
		measuredBy: "demo",
		particleData: makeParticleData(180, 350),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[1],
		x: 200,
		y: 200,
	},
	{
		id: "dm-1-2",
		trapPositionId: "demo-pos-1",
		collectorId: null,
		measuredAt: DEMO_TS[2],
		measuredBy: "demo",
		particleData: makeParticleData(250, 400),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[2],
		x: 200,
		y: 200,
	},
	{
		id: "dm-1-3",
		trapPositionId: "demo-pos-1",
		collectorId: null,
		measuredAt: DEMO_TS[3],
		measuredBy: "demo",
		particleData: makeParticleData(320, 450),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[3],
		x: 200,
		y: 200,
	},
	{
		id: "dm-1-4",
		trapPositionId: "demo-pos-1",
		collectorId: null,
		measuredAt: DEMO_TS[4],
		measuredBy: "demo",
		particleData: makeParticleData(380, 480),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[4],
		x: 200,
		y: 200,
	},
	{
		id: "dm-1-5",
		trapPositionId: "demo-pos-1",
		collectorId: null,
		measuredAt: DEMO_TS[5],
		measuredBy: "demo",
		particleData: makeParticleData(430, 500),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[5],
		x: 200,
		y: 200,
	},
	{
		id: "dm-1-6",
		trapPositionId: "demo-pos-1",
		collectorId: null,
		measuredAt: DEMO_TS[6],
		measuredBy: "demo",
		particleData: makeParticleData(480, 520),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[6],
		x: 200,
		y: 200,
	},
	{
		id: "dm-1-7",
		trapPositionId: "demo-pos-1",
		collectorId: null,
		measuredAt: DEMO_TS[7],
		measuredBy: "demo",
		particleData: makeParticleData(520, 550),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[7],
		x: 200,
		y: 200,
	},
	{
		id: "dm-1-8",
		trapPositionId: "demo-pos-1",
		collectorId: null,
		measuredAt: DEMO_TS[8],
		measuredBy: "demo",
		particleData: makeParticleData(560, 580),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[8],
		x: 200,
		y: 200,
	},
	{
		id: "dm-1-9",
		trapPositionId: "demo-pos-1",
		collectorId: null,
		measuredAt: DEMO_TS[9],
		measuredBy: "demo",
		particleData: makeParticleData(600, 600),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[9],
		x: 200,
		y: 200,
	},

	// pos-2 (TP-02): consistently in the yellow/orange zone
	{
		id: "dm-2-0",
		trapPositionId: "demo-pos-2",
		collectorId: null,
		measuredAt: DEMO_TS[0],
		measuredBy: "demo",
		particleData: makeParticleData(380, 420),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[0],
		x: 400,
		y: 200,
	},
	{
		id: "dm-2-1",
		trapPositionId: "demo-pos-2",
		collectorId: null,
		measuredAt: DEMO_TS[2],
		measuredBy: "demo",
		particleData: makeParticleData(400, 430),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[2],
		x: 400,
		y: 200,
	},
	{
		id: "dm-2-2",
		trapPositionId: "demo-pos-2",
		collectorId: null,
		measuredAt: DEMO_TS[4],
		measuredBy: "demo",
		particleData: makeParticleData(420, 450),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[4],
		x: 400,
		y: 200,
	},
	{
		id: "dm-2-3",
		trapPositionId: "demo-pos-2",
		collectorId: null,
		measuredAt: DEMO_TS[6],
		measuredBy: "demo",
		particleData: makeParticleData(440, 470),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[6],
		x: 400,
		y: 200,
	},
	{
		id: "dm-2-4",
		trapPositionId: "demo-pos-2",
		collectorId: null,
		measuredAt: DEMO_TS[8],
		measuredBy: "demo",
		particleData: makeParticleData(460, 480),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[8],
		x: 400,
		y: 200,
	},

	// pos-3 (TP-03): always green (well below limit)
	{
		id: "dm-3-0",
		trapPositionId: "demo-pos-3",
		collectorId: null,
		measuredAt: DEMO_TS[0],
		measuredBy: "demo",
		particleData: makeParticleData(50, 150),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[0],
		x: 600,
		y: 200,
	},
	{
		id: "dm-3-1",
		trapPositionId: "demo-pos-3",
		collectorId: null,
		measuredAt: DEMO_TS[2],
		measuredBy: "demo",
		particleData: makeParticleData(60, 180),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[2],
		x: 600,
		y: 200,
	},
	{
		id: "dm-3-2",
		trapPositionId: "demo-pos-3",
		collectorId: null,
		measuredAt: DEMO_TS[4],
		measuredBy: "demo",
		particleData: makeParticleData(80, 200),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[4],
		x: 600,
		y: 200,
	},
	{
		id: "dm-3-3",
		trapPositionId: "demo-pos-3",
		collectorId: null,
		measuredAt: DEMO_TS[6],
		measuredBy: "demo",
		particleData: makeParticleData(70, 190),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[6],
		x: 600,
		y: 200,
	},
	{
		id: "dm-3-4",
		trapPositionId: "demo-pos-3",
		collectorId: null,
		measuredAt: DEMO_TS[8],
		measuredBy: "demo",
		particleData: makeParticleData(90, 220),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[8],
		x: 600,
		y: 200,
	},

	// pos-4 (TP-04): confirmed but no measurements → gray in heatmap

	// pos-5 (TP-05): inactive, a few measurements before deactivation
	{
		id: "dm-5-0",
		trapPositionId: "demo-pos-5",
		collectorId: null,
		measuredAt: DEMO_TS[0],
		measuredBy: "demo",
		particleData: makeParticleData(200, 350),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[0],
		x: 500,
		y: 400,
	},
	{
		id: "dm-5-1",
		trapPositionId: "demo-pos-5",
		collectorId: null,
		measuredAt: DEMO_TS[2],
		measuredBy: "demo",
		particleData: makeParticleData(240, 380),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[2],
		x: 500,
		y: 400,
	},

	// ── Layout 1, Area 2 (Packaging Zone, limit=300) ───────────────
	// pos-6 (PK-01): over limit
	{
		id: "dm-6-0",
		trapPositionId: "demo-pos-6",
		collectorId: null,
		measuredAt: DEMO_TS[1],
		measuredBy: "demo",
		particleData: makeParticleData(280, 350),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[1],
		x: 200,
		y: 600,
	},
	{
		id: "dm-6-1",
		trapPositionId: "demo-pos-6",
		collectorId: null,
		measuredAt: DEMO_TS[3],
		measuredBy: "demo",
		particleData: makeParticleData(310, 400),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[3],
		x: 200,
		y: 600,
	},
	{
		id: "dm-6-2",
		trapPositionId: "demo-pos-6",
		collectorId: null,
		measuredAt: DEMO_TS[5],
		measuredBy: "demo",
		particleData: makeParticleData(340, 420),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[5],
		x: 200,
		y: 600,
	},
	{
		id: "dm-6-3",
		trapPositionId: "demo-pos-6",
		collectorId: null,
		measuredAt: DEMO_TS[7],
		measuredBy: "demo",
		particleData: makeParticleData(360, 450),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[7],
		x: 200,
		y: 600,
	},
	{
		id: "dm-6-4",
		trapPositionId: "demo-pos-6",
		collectorId: null,
		measuredAt: DEMO_TS[9],
		measuredBy: "demo",
		particleData: makeParticleData(380, 470),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[9],
		x: 200,
		y: 600,
	},

	// pos-7 (PK-02): green, well below limit
	{
		id: "dm-7-0",
		trapPositionId: "demo-pos-7",
		collectorId: null,
		measuredAt: DEMO_TS[1],
		measuredBy: "demo",
		particleData: makeParticleData(80, 120),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[1],
		x: 350,
		y: 600,
	},
	{
		id: "dm-7-1",
		trapPositionId: "demo-pos-7",
		collectorId: null,
		measuredAt: DEMO_TS[4],
		measuredBy: "demo",
		particleData: makeParticleData(100, 150),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[4],
		x: 350,
		y: 600,
	},
	{
		id: "dm-7-2",
		trapPositionId: "demo-pos-7",
		collectorId: null,
		measuredAt: DEMO_TS[7],
		measuredBy: "demo",
		particleData: makeParticleData(90, 140),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[7],
		x: 350,
		y: 600,
	},

	// ── Layout 1, Area 4 (Quality Control, limit=400) ──────────────
	// pos-8 (QC-01): yellow/orange zone
	{
		id: "dm-8-0",
		trapPositionId: "demo-pos-8",
		collectorId: null,
		measuredAt: DEMO_TS[2],
		measuredBy: "demo",
		particleData: makeParticleData(310, 400),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[2],
		x: 600,
		y: 600,
	},
	{
		id: "dm-8-1",
		trapPositionId: "demo-pos-8",
		collectorId: null,
		measuredAt: DEMO_TS[5],
		measuredBy: "demo",
		particleData: makeParticleData(340, 420),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[5],
		x: 600,
		y: 600,
	},
	{
		id: "dm-8-2",
		trapPositionId: "demo-pos-8",
		collectorId: null,
		measuredAt: DEMO_TS[8],
		measuredBy: "demo",
		particleData: makeParticleData(370, 440),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[8],
		x: 600,
		y: 600,
	},

	// pos-9 (QC-02): green
	{
		id: "dm-9-0",
		trapPositionId: "demo-pos-9",
		collectorId: null,
		measuredAt: DEMO_TS[2],
		measuredBy: "demo",
		particleData: makeParticleData(120, 200),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[2],
		x: 700,
		y: 700,
	},
	{
		id: "dm-9-1",
		trapPositionId: "demo-pos-9",
		collectorId: null,
		measuredAt: DEMO_TS[5],
		measuredBy: "demo",
		particleData: makeParticleData(150, 250),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[5],
		x: 700,
		y: 700,
	},
	{
		id: "dm-9-2",
		trapPositionId: "demo-pos-9",
		collectorId: null,
		measuredAt: DEMO_TS[8],
		measuredBy: "demo",
		particleData: makeParticleData(130, 220),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[8],
		x: 700,
		y: 700,
	},

	// ── Layout 2, Area 3 (ISO 7 Zone, limit=200) ───────────────────
	// pos-11 (CR-01): trending from green to red
	{
		id: "dm-11-0",
		trapPositionId: "demo-pos-11",
		collectorId: null,
		measuredAt: DEMO_TS[0],
		measuredBy: "demo",
		particleData: makeParticleData(50, 100),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[0],
		x: 150,
		y: 150,
	},
	{
		id: "dm-11-1",
		trapPositionId: "demo-pos-11",
		collectorId: null,
		measuredAt: DEMO_TS[2],
		measuredBy: "demo",
		particleData: makeParticleData(80, 130),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[2],
		x: 150,
		y: 150,
	},
	{
		id: "dm-11-2",
		trapPositionId: "demo-pos-11",
		collectorId: null,
		measuredAt: DEMO_TS[4],
		measuredBy: "demo",
		particleData: makeParticleData(130, 180),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[4],
		x: 150,
		y: 150,
	},
	{
		id: "dm-11-3",
		trapPositionId: "demo-pos-11",
		collectorId: null,
		measuredAt: DEMO_TS[6],
		measuredBy: "demo",
		particleData: makeParticleData(180, 220),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[6],
		x: 150,
		y: 150,
	},
	{
		id: "dm-11-4",
		trapPositionId: "demo-pos-11",
		collectorId: null,
		measuredAt: DEMO_TS[8],
		measuredBy: "demo",
		particleData: makeParticleData(220, 260),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[8],
		x: 150,
		y: 150,
	},
	{
		id: "dm-11-5",
		trapPositionId: "demo-pos-11",
		collectorId: null,
		measuredAt: DEMO_TS[9],
		measuredBy: "demo",
		particleData: makeParticleData(250, 280),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[9],
		x: 150,
		y: 150,
	},

	// pos-12 (CR-02): consistently green
	{
		id: "dm-12-0",
		trapPositionId: "demo-pos-12",
		collectorId: null,
		measuredAt: DEMO_TS[1],
		measuredBy: "demo",
		particleData: makeParticleData(40, 80),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[1],
		x: 300,
		y: 150,
	},
	{
		id: "dm-12-1",
		trapPositionId: "demo-pos-12",
		collectorId: null,
		measuredAt: DEMO_TS[4],
		measuredBy: "demo",
		particleData: makeParticleData(55, 100),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[4],
		x: 300,
		y: 150,
	},
	{
		id: "dm-12-2",
		trapPositionId: "demo-pos-12",
		collectorId: null,
		measuredAt: DEMO_TS[7],
		measuredBy: "demo",
		particleData: makeParticleData(60, 110),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[7],
		x: 300,
		y: 150,
	},

	// pos-13 (CR-03): orange zone
	{
		id: "dm-13-0",
		trapPositionId: "demo-pos-13",
		collectorId: null,
		measuredAt: DEMO_TS[1],
		measuredBy: "demo",
		particleData: makeParticleData(170, 250),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[1],
		x: 450,
		y: 150,
	},
	{
		id: "dm-13-1",
		trapPositionId: "demo-pos-13",
		collectorId: null,
		measuredAt: DEMO_TS[4],
		measuredBy: "demo",
		particleData: makeParticleData(190, 270),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[4],
		x: 450,
		y: 150,
	},
	{
		id: "dm-13-2",
		trapPositionId: "demo-pos-13",
		collectorId: null,
		measuredAt: DEMO_TS[7],
		measuredBy: "demo",
		particleData: makeParticleData(195, 280),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[7],
		x: 450,
		y: 150,
	},

	// pos-14 (CR-04): yellow zone
	{
		id: "dm-14-0",
		trapPositionId: "demo-pos-14",
		collectorId: null,
		measuredAt: DEMO_TS[2],
		measuredBy: "demo",
		particleData: makeParticleData(150, 200),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[2],
		x: 200,
		y: 350,
	},
	{
		id: "dm-14-1",
		trapPositionId: "demo-pos-14",
		collectorId: null,
		measuredAt: DEMO_TS[5],
		measuredBy: "demo",
		particleData: makeParticleData(165, 220),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[5],
		x: 200,
		y: 350,
	},
	{
		id: "dm-14-2",
		trapPositionId: "demo-pos-14",
		collectorId: null,
		measuredAt: DEMO_TS[8],
		measuredBy: "demo",
		particleData: makeParticleData(170, 230),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[8],
		x: 200,
		y: 350,
	},

	// ── Layout 2, Area 5 (ISO 5 Zone, limit=100) ───────────────────
	// pos-15 (ISO5-01): over limit
	{
		id: "dm-15-0",
		trapPositionId: "demo-pos-15",
		collectorId: null,
		measuredAt: DEMO_TS[3],
		measuredBy: "demo",
		particleData: makeParticleData(90, 150),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[3],
		x: 600,
		y: 200,
	},
	{
		id: "dm-15-1",
		trapPositionId: "demo-pos-15",
		collectorId: null,
		measuredAt: DEMO_TS[6],
		measuredBy: "demo",
		particleData: makeParticleData(110, 180),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[6],
		x: 600,
		y: 200,
	},
	{
		id: "dm-15-2",
		trapPositionId: "demo-pos-15",
		collectorId: null,
		measuredAt: DEMO_TS[9],
		measuredBy: "demo",
		particleData: makeParticleData(130, 200),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[9],
		x: 600,
		y: 200,
	},

	// pos-16 (ISO5-02): green
	{
		id: "dm-16-0",
		trapPositionId: "demo-pos-16",
		collectorId: null,
		measuredAt: DEMO_TS[3],
		measuredBy: "demo",
		particleData: makeParticleData(30, 50),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[3],
		x: 750,
		y: 200,
	},
	{
		id: "dm-16-1",
		trapPositionId: "demo-pos-16",
		collectorId: null,
		measuredAt: DEMO_TS[6],
		measuredBy: "demo",
		particleData: makeParticleData(40, 60),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[6],
		x: 750,
		y: 200,
	},
	{
		id: "dm-16-2",
		trapPositionId: "demo-pos-16",
		collectorId: null,
		measuredAt: DEMO_TS[9],
		measuredBy: "demo",
		particleData: makeParticleData(45, 70),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[9],
		x: 750,
		y: 200,
	},

	// ── Layout 3, Area 6 (Storage Area, limit=600) ─────────────────
	// pos-18 (WH-01): green
	{
		id: "dm-18-0",
		trapPositionId: "demo-pos-18",
		collectorId: null,
		measuredAt: DEMO_TS[0],
		measuredBy: "demo",
		particleData: makeParticleData(100, 200),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[0],
		x: 150,
		y: 150,
	},
	{
		id: "dm-18-1",
		trapPositionId: "demo-pos-18",
		collectorId: null,
		measuredAt: DEMO_TS[3],
		measuredBy: "demo",
		particleData: makeParticleData(150, 300),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[3],
		x: 150,
		y: 150,
	},
	{
		id: "dm-18-2",
		trapPositionId: "demo-pos-18",
		collectorId: null,
		measuredAt: DEMO_TS[6],
		measuredBy: "demo",
		particleData: makeParticleData(200, 350),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[6],
		x: 150,
		y: 150,
	},
	{
		id: "dm-18-3",
		trapPositionId: "demo-pos-18",
		collectorId: null,
		measuredAt: DEMO_TS[9],
		measuredBy: "demo",
		particleData: makeParticleData(250, 400),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[9],
		x: 150,
		y: 150,
	},

	// pos-19 (WH-02): yellow/orange trending to red
	{
		id: "dm-19-0",
		trapPositionId: "demo-pos-19",
		collectorId: null,
		measuredAt: DEMO_TS[0],
		measuredBy: "demo",
		particleData: makeParticleData(350, 450),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[0],
		x: 400,
		y: 150,
	},
	{
		id: "dm-19-1",
		trapPositionId: "demo-pos-19",
		collectorId: null,
		measuredAt: DEMO_TS[3],
		measuredBy: "demo",
		particleData: makeParticleData(450, 500),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[3],
		x: 400,
		y: 150,
	},
	{
		id: "dm-19-2",
		trapPositionId: "demo-pos-19",
		collectorId: null,
		measuredAt: DEMO_TS[6],
		measuredBy: "demo",
		particleData: makeParticleData(520, 550),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[6],
		x: 400,
		y: 150,
	},
	{
		id: "dm-19-3",
		trapPositionId: "demo-pos-19",
		collectorId: null,
		measuredAt: DEMO_TS[9],
		measuredBy: "demo",
		particleData: makeParticleData(620, 600),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[9],
		x: 400,
		y: 150,
	},

	// pos-20 (WH-03): consistently green
	{
		id: "dm-20-0",
		trapPositionId: "demo-pos-20",
		collectorId: null,
		measuredAt: DEMO_TS[1],
		measuredBy: "demo",
		particleData: makeParticleData(80, 150),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[1],
		x: 700,
		y: 150,
	},
	{
		id: "dm-20-1",
		trapPositionId: "demo-pos-20",
		collectorId: null,
		measuredAt: DEMO_TS[5],
		measuredBy: "demo",
		particleData: makeParticleData(100, 180),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[5],
		x: 700,
		y: 150,
	},
	{
		id: "dm-20-2",
		trapPositionId: "demo-pos-20",
		collectorId: null,
		measuredAt: DEMO_TS[9],
		measuredBy: "demo",
		particleData: makeParticleData(120, 220),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[9],
		x: 700,
		y: 150,
	},

	// pos-21 (WH-04): inactive, had measurements before deactivation
	{
		id: "dm-21-0",
		trapPositionId: "demo-pos-21",
		collectorId: null,
		measuredAt: DEMO_TS[0],
		measuredBy: "demo",
		particleData: makeParticleData(300, 400),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[0],
		x: 150,
		y: 400,
	},
	{
		id: "dm-21-1",
		trapPositionId: "demo-pos-21",
		collectorId: null,
		measuredAt: DEMO_TS[2],
		measuredBy: "demo",
		particleData: makeParticleData(400, 500),
		source: MeasurementSource.Manual,
		createdAt: DEMO_TS[2],
		x: 150,
		y: 400,
	},
];

function findLayout(id: string): FactoryLayout | undefined {
	return LAYOUTS.find((l) => l.id === id);
}

function findArea(layoutId: string, areaId: string): MonitoringArea | undefined {
	return findLayout(layoutId)?.areas?.find((a) => a.id === areaId);
}

function findPosition(id: string): { position: TrapPosition; area: MonitoringArea; layout: FactoryLayout } | undefined {
	for (const layout of LAYOUTS) {
		for (const area of layout.areas ?? []) {
			const pos = area.positions?.find((p) => p.id === id);
			if (pos) {
				return { position: pos, area, layout };
			}
		}
	}
	return undefined;
}

function now(): Date {
	return new Date();
}

/** All demo positions flattened for the import position matcher. */
function matchablePositions(): MatchablePosition[] {
	const result: MatchablePosition[] = [];
	for (const layout of LAYOUTS) {
		for (const area of layout.areas ?? []) {
			for (const position of area.positions ?? []) {
				result.push({ id: position.id, name: position.name, areaId: area.id, areaName: area.name });
			}
		}
	}
	return result;
}

/**
 * Extract the plain text of a PDF page by page, mirroring what the backend
 * does with unpdf. `unpdf` is imported lazily so the pdf.js bundle only
 * loads when the demo user actually imports a report.
 */
async function extractPdfPages(file: File): Promise<string[]> {
	const { extractText, getDocumentProxy } = await import("unpdf");
	const pdf = await getDocumentProxy(new Uint8Array(await file.arrayBuffer()));
	const { text } = await extractText(pdf, { mergePages: false });
	return Array.isArray(text) ? text : [text];
}

type ProbedImage = { objectUrl: string; width: number; height: number };

/**
 * Read the intrinsic dimensions of an uploaded image via a blob URL.
 *
 * The returned `objectUrl` is intentionally NOT revoked on success: demo
 * layouts live in the module-level `LAYOUTS` array for the whole session and
 * the UI renders `imageUrl` long after this call resolves. Revoking would
 * blank the floor plan. On decode failure the URL is revoked and empty
 * values are returned so the UI falls back to its "no image" placeholder.
 */
// NOTE: executor form is required here — this project compiles against
// `lib: ["dom", "es2022"]`, and `Promise.withResolvers` is ES2024.
function probeImage(file: File): Promise<ProbedImage> {
	const objectUrl = URL.createObjectURL(file);
	return new Promise<ProbedImage>((resolve) => {
		const img = new Image();
		img.onload = () => resolve({ objectUrl, width: img.naturalWidth, height: img.naturalHeight });
		img.onerror = () => {
			URL.revokeObjectURL(objectUrl);
			resolve({ objectUrl: "", width: 0, height: 0 });
		};
		img.src = objectUrl;
	});
}

/**
 * Deep-copy everything that leaves this service.
 *
 * NgRx's `strictStateImmutability` runtime check deep-freezes every object that
 * reaches the store. The demo seed lives in the module-level `LAYOUTS` /
 * `DEMO_MEASUREMENTS` / `DEMO_LIMITS` arrays, so handing those references out
 * would freeze the demo's own state and make every later mutation
 * (`LAYOUTS.push`, `Object.assign`, `splice`) throw — creating, renaming or
 * deleting a layout would silently do nothing.
 */
function copy<T>(value: T): T {
	return structuredClone(value);
}

@Injectable()
export class DemoEnvironmentApiService implements EnvironmentApiPort {
	/** Demo surface-report store: imported surface reports land here so the dashboard lists them. */
	private readonly demoSurfaceReports = inject(DemoSurfaceReportRepository);

	// ── Layouts ──────────────────────────────────────────────────────

	getLayouts(_siteId?: string): Observable<FactoryLayout[]> {
		return of(copy(LAYOUTS)).pipe(delay(300));
	}

	getLayout(id: string): Observable<FactoryLayout> {
		const layout = findLayout(id);
		if (!layout) {
			return of(copy({ ...DEMO_LAYOUT_1, id, name: "Unknown Layout" })).pipe(delay(200));
		}
		return of(copy(layout)).pipe(delay(200));
	}

	createLayout(name: string, siteId: string, imageFile: File, pixelsPerMeter?: number): Observable<FactoryLayout> {
		const created = probeImage(imageFile).then(({ objectUrl, width, height }) => {
			const layout: FactoryLayout = {
				id: `demo-layout-${Date.now()}`,
				siteId,
				name,
				imageUrl: objectUrl,
				imageWidth: width,
				imageHeight: height,
				pixelsPerMeter: pixelsPerMeter ?? 50,
				areas: [],
				createdAt: now(),
				updatedAt: now(),
			};
			LAYOUTS.push(layout);
			return copy(layout);
		});
		return from(created).pipe(delay(300));
	}

	updateLayout(id: string, data: UpdateFactoryLayoutDto): Observable<FactoryLayout> {
		const layout = findLayout(id);
		if (!layout) {
			return of(copy({ ...DEMO_LAYOUT_1, id, name: data.name ?? "Unknown", ...data } as FactoryLayout)).pipe(
				delay(200),
			);
		}
		Object.assign(layout, data, { updatedAt: now() });
		return of(copy(layout)).pipe(delay(200));
	}

	deleteLayout(id: string): Observable<void> {
		const idx = LAYOUTS.findIndex((l) => l.id === id);
		if (idx >= 0) {
			LAYOUTS.splice(idx, 1);
		}
		return of(void 0).pipe(delay(200));
	}

	// ── Areas ────────────────────────────────────────────────────────

	getAreas(layoutId: string): Observable<MonitoringArea[]> {
		const layout = findLayout(layoutId);
		return of(copy(layout?.areas ?? [])).pipe(delay(200));
	}

	createArea(layoutId: string, payload: CreateAreaPayload): Observable<MonitoringArea> {
		const area: MonitoringArea = {
			id: `demo-area-${Date.now()}`,
			layoutId,
			name: payload.name,
			polygonPoints: payload.polygonPoints ?? [],
			color: payload.color ?? "#6915ba",
			trapSpacingM: payload.trapSpacingM ?? 2,
			positions: [],
			createdAt: now(),
			updatedAt: now(),
		};
		const layout = findLayout(layoutId);
		if (layout) {
			layout.areas = [...(layout.areas ?? []), area];
		}
		return of(copy(area)).pipe(delay(250));
	}

	updateArea(areaId: string, payload: UpdateMonitoringAreaDto): Observable<MonitoringArea> {
		for (const layout of LAYOUTS) {
			const area = layout.areas?.find((a) => a.id === areaId);
			if (area) {
				Object.assign(area, payload, { updatedAt: now() });
				return of(copy(area)).pipe(delay(200));
			}
		}
		return of({
			id: areaId,
			layoutId: "demo-layout-1",
			name: payload.name ?? "Unknown",
			polygonPoints: payload.polygonPoints ?? [],
			color: payload.color ?? "#6915ba",
			trapSpacingM: payload.trapSpacingM ?? 2,
			positions: [],
			createdAt: now(),
			updatedAt: now(),
		}).pipe(delay(200));
	}

	deleteArea(areaId: string): Observable<void> {
		for (const layout of LAYOUTS) {
			const idx = layout.areas?.findIndex((a) => a.id === areaId);
			if (idx !== undefined && idx >= 0) {
				layout.areas?.splice(idx, 1);
				break;
			}
		}
		return of(void 0).pipe(delay(200));
	}

	// ── Positions ────────────────────────────────────────────────────

	getPositions(areaId: string): Observable<TrapPosition[]> {
		for (const layout of LAYOUTS) {
			const area = layout.areas?.find((a) => a.id === areaId);
			if (area) {
				return of(copy(area.positions ?? [])).pipe(delay(200));
			}
		}
		return of([]).pipe(delay(200));
	}

	getPositionById(id: string): Observable<PositionLookup> {
		const result = findPosition(id);
		if (!result) {
			const fallback: PositionLookup = {
				position: {
					id,
					areaId: "demo-area-1",
					name: "Unknown",
					x: 0,
					y: 0,
					status: TrapPositionStatus.Suggested,
					activatedAt: null,
					deactivatedAt: null,
					activeCollectorId: null,
					collectorBookedAt: null,
					notes: "",
					createdAt: now(),
					updatedAt: now(),
				},
				area: {
					id: "demo-area-1",
					layoutId: "demo-layout-1",
					name: "Unknown Area",
					polygonPoints: [],
					color: "#6915ba",
					trapSpacingM: 2,
					positions: [],
					createdAt: now(),
					updatedAt: now(),
				},
				layout: { ...DEMO_LAYOUT_1, id: "demo-layout-1", name: "Unknown Layout", areas: [] },
			};
			return of(fallback).pipe(delay(200));
		}
		return of(copy(result)).pipe(delay(200));
	}

	updatePosition(positionId: string, payload: UpdateTrapPositionDto): Observable<TrapPosition> {
		const result = findPosition(positionId);
		if (result) {
			Object.assign(result.position, payload, { updatedAt: now() });
			return of(copy(result.position)).pipe(delay(200));
		}
		return of(this.makeStubPosition(positionId, payload)).pipe(delay(200));
	}

	confirmPosition(positionId: string): Observable<TrapPosition> {
		const result = findPosition(positionId);
		const updated: TrapPosition = {
			...(result?.position ?? this.makeStubPosition(positionId)),
			status: TrapPositionStatus.Confirmed,
			updatedAt: now(),
		};
		if (result) {
			result.position.status = TrapPositionStatus.Confirmed;
			result.position.updatedAt = now();
		}
		return of(copy(updated)).pipe(delay(150));
	}

	activatePosition(positionId: string): Observable<TrapPosition> {
		const result = findPosition(positionId);
		const updated: TrapPosition = {
			...(result?.position ?? this.makeStubPosition(positionId)),
			status: TrapPositionStatus.Active,
			activatedAt: now(),
			updatedAt: now(),
		};
		if (result) {
			result.position.status = TrapPositionStatus.Active;
			result.position.activatedAt = now();
			result.position.updatedAt = now();
		}
		return of(copy(updated)).pipe(delay(150));
	}

	deactivatePosition(positionId: string): Observable<TrapPosition> {
		const result = findPosition(positionId);
		const updated: TrapPosition = {
			...(result?.position ?? this.makeStubPosition(positionId)),
			status: TrapPositionStatus.Inactive,
			deactivatedAt: now(),
			updatedAt: now(),
		};
		if (result) {
			result.position.status = TrapPositionStatus.Inactive;
			result.position.deactivatedAt = now();
			result.position.updatedAt = now();
		}
		return of(copy(updated)).pipe(delay(150));
	}

	deletePosition(positionId: string): Observable<void> {
		for (const layout of LAYOUTS) {
			for (const area of layout.areas ?? []) {
				const idx = area.positions?.findIndex((p) => p.id === positionId);
				if (idx !== undefined && idx >= 0) {
					area.positions?.splice(idx, 1);
					return of(void 0).pipe(delay(200));
				}
			}
		}
		return of(void 0).pipe(delay(200));
	}

	suggestPositions(areaId: string, payload: { spacingM: number }): Observable<TrapPosition[]> {
		for (const layout of LAYOUTS) {
			const area = layout.areas?.find((a) => a.id === areaId);
			if (area) {
				// Clear existing positions and generate new suggestions within the polygon
				area.positions = [];
				const pts = area.polygonPoints ?? [];
				let minX = Infinity,
					minY = Infinity,
					maxX = -Infinity,
					maxY = -Infinity;
				for (const p of pts) {
					minX = Math.min(minX, p.x);
					minY = Math.min(minY, p.y);
					maxX = Math.max(maxX, p.x);
					maxY = Math.max(maxY, p.y);
				}
				// Fallback if no polygon
				if (pts.length < 3) {
					minX = 50;
					minY = 50;
					maxX = 400;
					maxY = 400;
				}
				const ppm = layout.pixelsPerMeter ?? 100;
				const stepPx = payload.spacingM * ppm;
				const suggestions: TrapPosition[] = [];
				let idx = 0;
				for (let y = minY + stepPx / 2; y < maxY; y += stepPx) {
					for (let x = minX + stepPx / 2; x < maxX; x += stepPx) {
						const pos: TrapPosition = {
							id: `demo-suggested-${Date.now()}-${idx}`,
							areaId,
							name: `S${idx + 1}`,
							x: Math.round(x),
							y: Math.round(y),
							status: TrapPositionStatus.Suggested,
							activatedAt: null,
							deactivatedAt: null,
							activeCollectorId: null,
							collectorBookedAt: null,
							notes: "",
							createdAt: now(),
							updatedAt: now(),
						};
						suggestions.push(pos);
						area.positions.push(pos);
						idx++;
					}
				}
				return of(copy(suggestions)).pipe(delay(300));
			}
		}
		return of([]).pipe(delay(200));
	}

	// ── Collectors ───────────────────────────────────────────────────

	getActiveCollectors(): Observable<Collector[]> {
		return of(copy(DEMO_COLLECTORS)).pipe(delay(200));
	}

	bookCollector(positionId: string, payload: BookTrapCollectorDto): Observable<TrapPosition> {
		const result = findPosition(positionId);
		const updated: TrapPosition = {
			...(result?.position ?? this.makeStubPosition(positionId)),
			activeCollectorId: payload.collectorId,
			collectorBookedAt: now(),
			updatedAt: now(),
		};
		if (result) {
			result.position.activeCollectorId = payload.collectorId;
			result.position.collectorBookedAt = now();
			result.position.updatedAt = now();
		}
		return of(copy(updated)).pipe(delay(150));
	}

	// ── Measurements ─────────────────────────────────────────────────

	createMeasurement(positionId: string, payload: CreateMeasurementPayload): Observable<TrapMeasurement> {
		const measurement: TrapMeasurement = {
			id: `demo-meas-${Date.now()}`,
			trapPositionId: positionId,
			collectorId: payload.collectorId ?? null,
			measuredAt: new Date(payload.measuredAt),
			measuredBy: payload.measuredBy,
			particleData: payload.particleData ?? null,
			source: payload.source as MeasurementSource,
			createdAt: now(),
		};
		// Persist to in-memory store
		const pos = findPosition(positionId);
		DEMO_MEASUREMENTS.push({
			...measurement,
			x: pos?.position.x ?? 0,
			y: pos?.position.y ?? 0,
		} as DemoMeasurement);
		return of(copy(measurement)).pipe(delay(300));
	}

	getMeasurements(positionId: string): Observable<TrapMeasurement[]> {
		const result = DEMO_MEASUREMENTS.filter((m) => m.trapPositionId === positionId).map((m) => ({
			id: m.id,
			trapPositionId: m.trapPositionId,
			collectorId: m.collectorId,
			measuredAt: m.measuredAt,
			measuredBy: m.measuredBy,
			particleData: m.particleData,
			source: m.source,
			createdAt: m.createdAt,
		}));
		return of(copy(result)).pipe(delay(200));
	}

	getMeasurementsForArea(areaId: string): Observable<AreaMeasurement[]> {
		// Find all position IDs belonging to this area
		const areaPositionIds = new Set<string>();
		for (const layout of LAYOUTS) {
			for (const area of layout.areas ?? []) {
				if (area.id === areaId) {
					for (const pos of area.positions ?? []) {
						areaPositionIds.add(pos.id);
					}
				}
			}
		}
		const result = DEMO_MEASUREMENTS.filter((m) => areaPositionIds.has(m.trapPositionId));
		return of(copy(result)).pipe(delay(200));
	}

	updateMeasurement(measurementId: string, payload: UpdateTrapMeasurementDto): Observable<TrapMeasurement> {
		const measurement: TrapMeasurement = {
			id: measurementId,
			trapPositionId: "demo-pos-1",
			collectorId: payload.collectorId ?? null,
			measuredAt: payload.measuredAt ? new Date(payload.measuredAt) : now(),
			measuredBy: payload.measuredBy ?? "demo",
			particleData: payload.particleData ?? null,
			source: (payload.source as MeasurementSource) ?? MeasurementSource.Manual,
			createdAt: now(),
		};
		return of(copy(measurement)).pipe(delay(200));
	}

	deleteMeasurement(measurementId: string): Observable<void> {
		const idx = DEMO_MEASUREMENTS.findIndex((m) => m.id === measurementId);
		if (idx >= 0) {
			DEMO_MEASUREMENTS.splice(idx, 1);
		}
		return of(void 0).pipe(delay(200));
	}

	// ── Lab report import ────────────────────────────────────────────

	parseLabReports(_siteId: string, files: File[]): Observable<ParsedLabReportFileDto[]> {
		// Demo runs without a backend, so the PDFs are parsed in the browser
		// with the very same parser the backend uses — the review step shows
		// the real activation date, TCV/MCV/FCV and size classes of the file.
		return from(this.parseLabReportsInBrowser(files)).pipe(delay(300));
	}

	/** Extract the text of each PDF and run the shared CleanControlling parser. */
	private async parseLabReportsInBrowser(files: File[]): Promise<ParsedLabReportFileDto[]> {
		const positions = matchablePositions();
		const results: ParsedLabReportFileDto[] = [];

		for (const file of files) {
			try {
				const pages = await extractPdfPages(file);
				const { parsed, warnings } = parseLabReport(pages);
				const { candidates, bestMatchPositionId } = rankPositionCandidates(
					parsed.samplingSite,
					parsed.trapNo,
					positions,
				);
				results.push({
					fileName: file.name,
					status: "parsed",
					warnings,
					blobPath: `demo/import/${file.name}`,
					parsed,
					duplicateOfReportId: null,
					positionCandidates: candidates,
					bestMatchPositionId,
				});
			} catch (error) {
				results.push({
					fileName: file.name,
					status: "parse_failed",
					warnings: [error instanceof Error ? error.message : "Unknown parse error"],
					blobPath: null,
					parsed: null,
					duplicateOfReportId: null,
					positionCandidates: [],
					bestMatchPositionId: null,
				});
			}
		}
		return results;
	}

	commitLabReportImport(_siteId: string, items: LabReportCommitItemDto[]): Observable<LabReportCommitResultDto[]> {
		const results: LabReportCommitResultDto[] = items.map((item, i) => ({
			fileName: item.fileName,
			analysisNo: item.analysisNo ?? null,
			status: "created" as const,
			reportId: `demo-report-${Date.now()}-${i}`,
			measurementId: `demo-measurement-${Date.now()}-${i}`,
			positionId: item.positionId ?? "demo-pos-1",
			error: null,
		}));
		return of(results).pipe(delay(400));
	}

	// ── Surface report import (Jomesa PicEd ASCII) ────────────────────

	parseSurfaceReports(_siteId: string, files: File[]): Observable<ParsedSurfaceReportFileDto[]> {
		// Demo runs without a backend, so the PicEd Cora ASCII exports are
		// parsed in the browser with the very same parser the backend uses.
		return from(this.parseSurfaceReportsInBrowser(files)).pipe(delay(300));
	}

	/** Decode each Latin-1 export and run the shared PicEd Cora parser. */
	private async parseSurfaceReportsInBrowser(files: File[]): Promise<ParsedSurfaceReportFileDto[]> {
		const results: ParsedSurfaceReportFileDto[] = [];

		for (const file of files) {
			const relativePath = file.webkitRelativePath || file.name;
			try {
				// PicEd Cora exports are ISO-8859-1 encoded (µ = 0xB5).
				const text = new TextDecoder("iso-8859-1").decode(await file.arrayBuffer());
				const { parsed, warnings } = parsePicedAsciiReport(text);
				results.push({
					fileName: relativePath,
					status: "parsed",
					warnings,
					blobPath: `demo/import/surface/${relativePath}`,
					parsed: {
						analysisNo: this.surfaceAnalysisNo(relativePath),
						partId: null,
						examiner: null,
						dateOfAnalysis: parsed.dateOfAnalysis?.toISOString() ?? null,
						componentSurfaceAreaCm2: parsed.examinedAreaCm2,
						weightMg: null,
						comment: null,
						totalCount: parsed.totalCount,
						sizeClasses: parsed.sizeClasses.map((sizeClass) => ({
							start: sizeClass.start,
							end: Number.isFinite(sizeClass.end) ? sizeClass.end : null,
							metallic: sizeClass.metallic,
							nonMetallic: sizeClass.nonMetallic,
							fibers: sizeClass.fibers,
						})),
					},
					duplicateOfReportId:
						this.demoSurfaceReports.findByMeasurementSystemId(this.surfaceAnalysisNo(relativePath))?.id ?? null,
				});
			} catch (error) {
				results.push({
					fileName: relativePath,
					status: "parse_failed",
					warnings: [error instanceof Error ? error.message : "Unknown parse error"],
					blobPath: null,
					parsed: null,
					duplicateOfReportId: null,
				});
			}
		}
		return results;
	}

	/**
	 * The Jomesa analysis id: the containing folder name when the file came
	 * from a folder upload (e.g. "637B2BB3/ascii.txt" → "637B2BB3"), the
	 * base name without extension otherwise — same rule as the backend.
	 */
	private surfaceAnalysisNo(relativePath: string): string {
		const segments = relativePath.split("/").filter((s) => s !== "");
		if (segments.length > 1) return segments[segments.length - 2];
		return (segments[0] ?? relativePath).replace(/\.[^.]+$/, "");
	}

	commitSurfaceReportImport(
		siteId: string,
		items: SurfaceReportCommitItemDto[],
	): Observable<SurfaceReportCommitResultDto[]> {
		// Persist into the demo surface-report store so imported reports show
		// up on the dashboard, mirroring the backend's SurfaceReportDbService.
		return from(this.commitSurfaceReportsToDemoStore(siteId, items)).pipe(delay(400));
	}

	private async commitSurfaceReportsToDemoStore(
		siteId: string,
		items: SurfaceReportCommitItemDto[],
	): Promise<SurfaceReportCommitResultDto[]> {
		const results: SurfaceReportCommitResultDto[] = [];
		for (const item of items) {
			const analysisNo = item.analysisNo ?? null;
			const duplicate = analysisNo ? this.demoSurfaceReports.findByMeasurementSystemId(analysisNo) : null;
			if (duplicate && !item.force) {
				results.push({
					fileName: item.fileName,
					analysisNo,
					status: "skipped_duplicate",
					reportId: duplicate.id,
					error: null,
				});
				continue;
			}
			const bins = (item.sizeClasses ?? []).map((sc) => this.toSurfaceSizeClassBin(sc));
			const report = await firstValueFrom(
				this.demoSurfaceReports.createImported(
					{
						siteId,
						measurementSystemVendor: "Jomesa PicEd",
						measurementSystemId: analysisNo,
						partId: item.partId ?? null,
						examiner: item.examiner ?? null,
						dateOfAnalysis: item.dateOfAnalysis ? new Date(item.dateOfAnalysis) : null,
						componentSurfaceAreaCm2: item.componentSurfaceAreaCm2 ?? null,
						weightMg: item.weightMg ?? null,
						comment: item.comment ?? null,
						pdfBlobPath: item.blobPath ?? null,
						manuallyEdited: false,
					},
					bins,
				),
			);
			results.push({ fileName: item.fileName, analysisNo, status: "created", reportId: report.id, error: null });
		}
		return results;
	}

	/** Build a domain size-class bin from an imported size-class count (same pattern as the backend). */
	private toSurfaceSizeClassBin(sc: SurfaceImportSizeClassDto): SizeClassBin {
		const label = sc.end == null ? `> ${sc.start} µm` : `${sc.start}–${sc.end} µm`;
		const bin = emptySizeClassBin(toSizeClass({ lower: sc.start, upper: sc.end, label }));
		bin.materialMap.all[Material.METAL] = sc.metallic;
		bin.materialMap.all[Material.NON_METAL] = sc.nonMetallic;
		bin.materialMap.all[Material.FIBER] = sc.fibers;
		bin.count.all = sc.metallic + sc.nonMetallic + sc.fibers;
		return bin;
	}

	// ── Limits ───────────────────────────────────────────────────────

	createLimit(payload: CreateTrapLimitDto): Observable<TrapLimit> {
		const limit: TrapLimit = {
			id: `demo-limit-${Date.now()}`,
			areaId: payload.areaId,
			materialType: payload.materialType,
			limitType: payload.limitType,
			value: payload.value,
			createdAt: now(),
			updatedAt: now(),
		};
		DEMO_LIMITS.push(limit);
		return of(copy(limit)).pipe(delay(200));
	}

	getLimitsForArea(areaId: string): Observable<TrapLimit[]> {
		return of(copy(DEMO_LIMITS.filter((l) => l.areaId === areaId))).pipe(delay(200));
	}

	updateLimit(limitId: string, payload: UpdateTrapLimitDto): Observable<TrapLimit> {
		const idx = DEMO_LIMITS.findIndex((l) => l.id === limitId);
		if (idx >= 0) {
			Object.assign(DEMO_LIMITS[idx], payload, { updatedAt: now() });
			return of(copy(DEMO_LIMITS[idx])).pipe(delay(200));
		}
		const limit: TrapLimit = {
			id: limitId,
			areaId: payload.areaId ?? "",
			materialType: payload.materialType ?? MaterialType.Metal,
			limitType: payload.limitType ?? LimitType.FeretMax,
			value: payload.value ?? 0,
			createdAt: now(),
			updatedAt: now(),
		};
		return of(limit).pipe(delay(200));
	}

	deleteLimit(limitId: string): Observable<void> {
		const idx = DEMO_LIMITS.findIndex((l) => l.id === limitId);
		if (idx >= 0) {
			DEMO_LIMITS.splice(idx, 1);
		}
		return of(void 0).pipe(delay(200));
	}

	// ── Helpers ──────────────────────────────────────────────────────

	private makeStubPosition(positionId: string, payload?: UpdateTrapPositionDto): TrapPosition {
		return {
			id: positionId,
			areaId: payload?.areaId ?? "",
			name: payload?.name ?? "",
			x: payload?.x ?? 0,
			y: payload?.y ?? 0,
			status: (payload?.status as TrapPositionStatus) ?? TrapPositionStatus.Suggested,
			activatedAt: null,
			deactivatedAt: null,
			activeCollectorId: null,
			collectorBookedAt: null,
			notes: "",
			createdAt: now(),
			updatedAt: now(),
		};
	}
}
